import React from "react";

/**
 * Design-system Button.
 *
 * Variants: primary | secondary | ghost | danger | success | subtle
 * Sizes:    sm | md | lg | icon
 *
 * Behaviour is a plain <button>; all native props (onClick, type, disabled,
 * aria-*) are forwarded untouched.
 */
const VARIANTS = {
    primary:
        "bg-primary-600 text-white shadow-soft hover:bg-primary-700 active:bg-primary-800 focus-visible:ring-primary-500/40",
    secondary:
        "bg-white text-slate-700 border border-slate-200 shadow-soft hover:bg-slate-50 hover:border-slate-300 active:bg-slate-100 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800",
    ghost:
        "bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white",
    danger:
        "bg-danger-600 text-white shadow-soft hover:bg-danger-700 active:bg-danger-800 focus-visible:ring-danger-500/40",
    success:
        "bg-success-600 text-white shadow-soft hover:bg-success-700 active:bg-success-800 focus-visible:ring-success-500/40",
    subtle:
        "bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-500/10 dark:text-primary-300 dark:hover:bg-primary-500/20",
};

const SIZES = {
    sm: "h-8 px-3 text-xs gap-1.5 rounded-lg",
    md: "h-10 px-4 text-sm gap-2 rounded-xl",
    lg: "h-11 px-5 text-sm gap-2 rounded-xl",
    icon: "h-9 w-9 rounded-lg justify-center",
};

export default function Button({
    children,
    variant = "primary",
    size = "md",
    className = "",
    type = "button",
    loading = false,
    disabled = false,
    icon: Icon = null,
    ...props
}) {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={`inline-flex items-center justify-center font-medium transition-all duration-200
                focus-visible:outline-none focus-visible:ring-4
                disabled:opacity-50 disabled:pointer-events-none
                ${VARIANTS[variant] || VARIANTS.primary}
                ${SIZES[size] || SIZES.md}
                ${className}`}
            {...props}
        >
            {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
                Icon && <Icon className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
            )}
            {children}
        </button>
    );
}
